import { useState } from 'react';
import type { ContentRouteFolderEntry, SiblingFolderLink } from '@/lib/api/content-route-index.server';
import type { ContentFolderSearchResult } from '@/lib/api/content-search.server';
import EatalyCategoryPlpStickyBar from '@/components/eataly-category-plp-sticky-bar';
import EatalyCategoryPlpOrdinationDrawer from '@/components/eataly-category-plp-ordination-drawer';
import ContentFolderListingHeader from '@/components/content-folder-listing/content-folder-listing-header';
import ContentFolderListingGrid from '@/components/content-folder-listing/content-folder-listing-grid';
import { useContentFolderHeaderOptimisticState } from '@/components/content-folder-listing/use-content-folder-header-optimistic-state';
import { useContentFolderListingNavigation } from '@/components/content-folder-listing/use-content-folder-listing-navigation';
import { useOrdinationPanelState } from '@/hooks/use-ordination-panel-state';
import { useListingPaginationScroll } from '@/hooks/use-listing-pagination-scroll';
import { getNonCriticalSkeletonCount } from '@/lib/search-product-grid';
import {
    getContentFolderMaxNonCriticalSkeletonCount,
    getContentFolderMinLoadingSkeletonCount,
} from '@/lib/content-folder-listing-hits';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useDeferredRender } from '@/hooks/use-deferred-render';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import SeoLinkingSection from '@/components/seo-linking/seo-linking-section';
import type { PageWithComponentData } from 'src/lib/page-designer/page-loader.server';
import { Region } from '@/components/region';

type ContentFolderListingProps = {
    folder: ContentRouteFolderEntry;
    page?: PageWithComponentData;
    kind: 'folderListing' | 'newsListing';
    criticalSearchResult: ContentFolderSearchResult;
    nonCriticalSearchResult?: Promise<ContentFolderSearchResult>;
    subCategoryLinks: SiblingFolderLink[];
    currentPage: number;
    limit: number;
    initialOrdinationOpen: boolean;
    sortingOptionIds: string[];
};

export default function ContentFolderListing({
    folder,
    page,
    kind,
    criticalSearchResult,
    nonCriticalSearchResult,
    subCategoryLinks,
    currentPage,
    limit,
    initialOrdinationOpen,
    sortingOptionIds,
}: ContentFolderListingProps) {
    const { t: tSeoLinking } = useTranslation('seoLinking');
    const { listingTopRef, isListingLoading } = useContentFolderListingNavigation();
    useListingPaginationScroll(listingTopRef);

    const {
        title,
        description,
        showDescriptionSkeleton,
        showSubcategoriesSkeleton,
        showTitleSkeleton,
        setPendingNavigationTarget,
    } = useContentFolderHeaderOptimisticState(folder);
    const [ordinationOpen, toggleOrdinationOpen, handleOrdinationOpenChange] =
        useOrdinationPanelState(initialOrdinationOpen);
    const [pageNumber, setPageNumber] = useState(currentPage);
    const isSquaredCardLayout = folder.c_folderListingLayout === 'squared-card-layout';
    const gridClassName = cn(
        ' pt-6 md:pt-10 pb-8 grid  lg:gap-x-1 ',
        isSquaredCardLayout
            ? 'grid-cols-2 lg:grid-cols-4 gap-y-8 gap-x-1 lg:gap-y-6'
            : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-4 lg:gap-y-6'
    );
    const isDesktop = useMediaQuery('(min-width: 1024px)');
    const config = useConfig();
    const maxSkeletonCount = getContentFolderMaxNonCriticalSkeletonCount(
        config.search.content.skeletons,
        isDesktop,
        folder.c_folderListingLayout
    );
    const criticalCount = criticalSearchResult?.hits?.length ?? 0;
    const resolvedNonCriticalCount = getNonCriticalSkeletonCount({
        maxSkeletons: maxSkeletonCount,
        limit,
        criticalCount,
        total: criticalSearchResult?.total ?? 0,
        offset: criticalSearchResult?.offset ?? 0,
    });
    const nonCriticalCount = resolvedNonCriticalCount;
    const loadingSkeletonCount = Math.max(
        (criticalSearchResult.hits?.length ?? 0) + nonCriticalCount,
        getContentFolderMinLoadingSkeletonCount(folder.c_folderListingLayout)
    );
    const shouldRenderNonCritical = useDeferredRender(nonCriticalCount > 0);

    return (
        <div className="relative" ref={listingTopRef}>
            <ContentFolderListingHeader
                title={title}
                description={description}
                showTitleSkeleton={showTitleSkeleton}
                showDescriptionSkeleton={showDescriptionSkeleton}
                showSubcategoriesSkeleton={showSubcategoriesSkeleton}
                subCategoryLinks={subCategoryLinks}
                onNavigateSubcategory={setPendingNavigationTarget}
                isSquaredCardLayout={isSquaredCardLayout}
            />
            {!isSquaredCardLayout && (
                <EatalyCategoryPlpStickyBar
                    disableFilters
                    result={criticalSearchResult}
                    ordinationOpen={ordinationOpen}
                    onToggleOrdination={toggleOrdinationOpen}
                    onOrdinationOpenChange={handleOrdinationOpenChange}
                    sortingOptionIds={sortingOptionIds}
                    countLabelKey="categoryPlp:contentFolderListing.itemsAvailable"
                    paginationLimit={limit}
                    pageNumber={pageNumber}
                />
            )}
            <EatalyCategoryPlpOrdinationDrawer
                open={ordinationOpen}
                onOpenChange={handleOrdinationOpenChange}
                result={criticalSearchResult}
                sortingOptionIds={sortingOptionIds}
            />
            <ContentFolderListingGrid
                gridClassName={gridClassName}
                page={page}
                kind={kind}
                isListingLoading={isListingLoading}
                loadingSkeletonCount={loadingSkeletonCount}
                criticalSearchResult={criticalSearchResult}
                nonCriticalSearchResult={nonCriticalSearchResult}
                shouldRenderNonCritical={shouldRenderNonCritical}
                nonCriticalCount={nonCriticalCount}
                criticalCount={criticalCount}
                currentPage={currentPage}
                limit={limit}
                setPageNumber={setPageNumber}
                cardVariant={isSquaredCardLayout ? 'square' : 'standard'}
            />
            {folder.c_secondaryDescription && (
                <div className="grid-global">
                    <div className="col-span-6 lg:col-start-2 lg:col-span-10 body-copy-l text-center pb-6 lg:pb-12">
                        {/* eslint-disable-next-line react/no-danger -- BM/PIM-authored HTML */}
                        <div dangerouslySetInnerHTML={{ __html: folder.c_secondaryDescription ?? '' }} />
                    </div>
                </div>
            )}
            {kind === 'newsListing' && page && <Region page={page} regionId="pressOffice" />}
            <SeoLinkingSection
                title={tSeoLinking('sectionTitle')}
                navAriaLabel={tSeoLinking('sectionTitle')}
                links={subCategoryLinks.map((link) => ({
                    name: link.title,
                    id: link.id,
                    to: link.path,
                }))}
            />
        </div>
    );
}
